import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { CreateTextModalComponent } from './create-text-modal/create-text-modal.component';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { findSmsContent } from '../optional-setting/sms-delivery.mapper';

declare let SegmentedMessage: any;

/**
 * TextMessageComponent provides a UI to view, create, edit, and remove SMS/text message content for a campaign; it manages a modal for creating/editing SMS content and emits events when content changes.
 * @component
 * @selector app-text-message
 * @example
 *   <app-text-message
 *     [isExpanded]="true"
 *     [campaignData]="campaign"
 *     [isCompleted]="false"
 *     (toggle)="onToggle($event)"
 *     (done)="onDone()"
 *     (dataChange)="onDataChange($event)"
 *     (dataClear)="onDataClear()"
 *   ></app-text-message>
 * @input boolean isExpanded - Controls whether the component's collapsible section is expanded (default true).
 * @input any campaignData - The campaign object (expected to contain contents.sms) used to prefill and detect existing SMS data.
 * @input boolean isCompleted - Flag indicating whether this step is completed (affects UI state).
 * @output EventEmitter<boolean> toggle - Emitted when the collapsible section is toggled; payload is the new expanded state.
 * @output EventEmitter<void> done - Emitted when the user finishes the "Done" action (used when SMS data already exists).
 * @output EventEmitter<any> dataChange - Emitted with the current SMS payload ({ senderId, textMessage, linkText }) when the form or modal saves changes.
 * @output EventEmitter<void> dataClear - Emitted when SMS data is cleared/deleted.
 *
 * Key methods:
 *  - onTextMessageToggle(expanded: boolean): Emits toggle to notify parent of expanded/collapsed state.
 *  - onTextMessageDone(): If no SMS exists opens the create modal; otherwise emits done.
 *  - onCreateTextMessage(): Opens the create/edit modal and sets edit mode based on existing data.
 *  - onSaveTextMessage(campaignData: any): Saves data from the modal, shows success messages (create vs update), updates local state, emits dataChange, and closes modal.
 *  - onCloseModal(): Closes the create/edit modal and resets edit mode.
 *  - onEditMessage(): Placeholder for edit behavior (currently logs a message and sets edit mode flow via modal).
 *  - onDeleteMessage(): Clears local SMS data, logs deletion, and emits dataClear.
 *  - onFormChange(): Aggregates senderId, textMessage, and linkText into an SMS payload and emits dataChange.
 *  - get hasSmsData(): Boolean getter that determines whether meaningful SMS content exists either in incoming campaignData.contents.sms or local textMessageData.
 *
 * Lifecycle hooks:
 *  - ngOnInit(): Initializes local textMessageData from campaignData.contents.sms if present.
 *  - ngOnChanges(): Mirrors ngOnInit behavior to update local fields when campaignData changes externally.
 *
 * Notes:
 *  - A modal (CreateTextModalComponent) is used for creating/updating SMS content; isEditMode distinguishes between create and update flows.
 *  - NotificationService is used to show success toasts on create/update.
 */
@Component({
  selector: 'app-text-message',
  imports: [CommonModule, CollapsibleSectionComponent, CreateTextModalComponent],
  templateUrl: './text-message.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './text-message.component.scss',
})
export class TextMessageComponent implements OnInit, OnChanges {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();
  @Output() mmsDataChange = new EventEmitter<any>();
  @Output() dataClear = new EventEmitter<void>();
  @Output() mmsCampaignDataClear = new EventEmitter<void>();

  isModalOpen = false;
  textMessageData: any = {
    senderId: '',
    textMessage: '',
    linkText: '',
  };
  isEditMode = false;

  constructor(private messageService: NotificationService) {}

  /**
   * Initialize text message data from existing campaign data if available.
   * @example
   * gOnInit()
   * undefined
   * @returns {{void}} Does not return a value; initializes the component's textMessageData based on this.campaignData.contents.sms when present.
   **/
  ngOnInit(): void {
    this.syncFromCampaignData();
  }

  /**
   * Update the component's text message form fields from the current campaignData when it changes.
   * @example
   * gOnChanges()
   * // If this.campaignData.contents.sms exists, this.textMessageData will be populated:
   * // { senderId: '...', textMessage: '...', linkText: '...' }
   * @returns {void} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaignData']) {
      this.syncFromCampaignData();
    }
  }

  private syncFromCampaignData(): void {
    const smsContent = findSmsContent(this.campaignData?.contents);
    if (smsContent) {
      this.textMessageData = {
        messageType: 'SMS',
        senderId: smsContent.sms_sender_contact || '',
        textMessage: smsContent.sms_body || '',
        linkText: smsContent.sms_link || '',
      };
      return;
    }

    const mmsContent = this.findMmsCampaignContent(this.campaignData?.contents);
    if (mmsContent) {
      const cfg = mmsContent.mms_config || {};
      const bubble1 = cfg.bubble_1_coupon || {};
      const fallback = cfg.mms_fallback || {};
      const hasBubble2 = !!cfg.bubble_2_text_body?.trim();
      this.textMessageData = {
        messageType: 'MMS',
        mmsAltText: bubble1.image_alt_text || '',
        mmsCaption: bubble1.image_caption || '',
        mmsUrl: cfg.mms_link || '',
        mmsFallbackBehavior: (fallback.behavior || 'send_sms_with_image_link').replace(/_/g, '-'),
        mmsFallbackText: fallback.text || '',
        mmsBubble2Text: cfg.bubble_2_text_body || '',
        splitTwoBubbles: hasBubble2,
        mmsImageUrl: bubble1.coupon_image_url || '',
        mmsImageWidth: bubble1.image_width || 0,
        mmsImageHeight: bubble1.image_height || 0,
      };
    }
  }

  private findMmsCampaignContent(contents: any): any {
    if (!Array.isArray(contents)) return null;
    return contents.find((c: any) => c.platform === 'mms_campaign') || null;
  }

  onTextMessageToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onTextMessageDone(): void {
    if (!this.hasSmsData && !this.hasMmsData) {
      this.onCreateTextMessage();
    } else {
      this.done.emit();
    }
  }

  onEditMessage(): void {
    // TODO: Implement edit message functionality
    console.log('Edit message clicked');
  }

  onDeleteMessage(): void {
    this.textMessageData = { senderId: '', textMessage: '', linkText: '' };
    this.dataClear.emit();
    this.mmsCampaignDataClear.emit();
  }

  onCreateTextMessage(): void {
    this.isEditMode = this.hasSmsData || this.hasMmsData;
    this.isModalOpen = true;
  }

  onCloseModal(): void {
    this.isModalOpen = false;
    // Reset edit mode flag when closing modal
    this.isEditMode = false;
  }

  /**
   * Saves or updates a text message (SMS) campaign, closes the modal, shows a success toast, emits a change event, and resets edit mode.
   * @example
   * nSaveTextMessage({ id: 123, title: 'Promo', message: 'Buy now' })
   * undefined
   * @param {{any}} {{campaignData}} - The text message campaign payload to save or update.
   * @returns {{void}} No value is returned.
   **/
  onSaveTextMessage(campaignData: any): void {
    console.log('Text message campaign saved:', campaignData);
    this.textMessageData = { ...campaignData };
    this.isModalOpen = false;

    if (!this.isEditMode) {
      this.messageService.add({
        severity: 'success',
        summary: 'Text message campaign created!',
        detail: 'A new SMS campaign has been created successfully.',
      });
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'Text message campaign updated!',
        detail: 'The SMS campaign has been updated successfully.',
      });
    }

    if (campaignData.messageType === 'MMS') {
      // Clear SMS content so both channels are never present at the same time
      this.dataClear.emit();
      this.mmsDataChange.emit({
        platform: 'mms_campaign',
        mms_config: campaignData.mms_config,
      });
    } else {
      // Clear mms_campaign content so both channels are never present at the same time
      this.mmsCampaignDataClear.emit();
      this.onFormChange();
    }

    this.isEditMode = false;
  }

  onFormChange(): void {
    const smsData = {
      senderId: this.textMessageData.senderId,
      textMessage: this.textMessageData.textMessage,
      linkText: this.textMessageData.linkText,
    };

    this.dataChange.emit(smsData);
  }

  /**
   * Check whether SMS content exists with meaningful fields in campaignData or local textMessageData.
   * @example
   * hasSmsData()
   * true
   * @returns {{boolean}} Whether SMS data (sms_sender_contact, sms_body, sms_link or local senderId/textMessage/linkText) is present.
   **/
  get hasSmsData(): boolean {
    const smsContent = findSmsContent(this.campaignData?.contents);
    if (smsContent) {
      return Boolean(
        smsContent.sms_sender_contact?.trim() ||
        smsContent.sms_body?.trim() ||
        smsContent.sms_link?.trim()
      );
    }
    return Boolean(
      this.textMessageData.senderId?.trim() ||
      this.textMessageData.textMessage?.trim() ||
      this.textMessageData.linkText?.trim()
    );
  }

  get hasMmsData(): boolean {
    const mmsContent = this.findMmsCampaignContent(this.campaignData?.contents);
    if (mmsContent) {
      const cfg = mmsContent.mms_config || {};
      return Boolean(
        cfg.bubble_1_coupon?.coupon_image_url?.trim() ||
        cfg.bubble_2_text_body?.trim() ||
        cfg.mms_fallback?.text?.trim()
      );
    }
    return (
      this.textMessageData?.messageType === 'MMS' &&
      Boolean(
        this.textMessageData.mmsFallbackText?.trim() || this.textMessageData.mmsImageUrl?.trim()
      )
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  private buildSegmentParts(text: string): string[] {
    if (!text || !text.trim()) return [''];
    try {
      if (typeof SegmentedMessage !== 'undefined') {
        const msg = new SegmentedMessage(text);
        if (msg.segmentsCount <= 1) return [text];
        return (msg.segments as any[][]).map((seg: any[]) => seg.map((c: any) => c.raw).join(''));
      }
    } catch {
      /* ignore */
    }
    return [text];
  }

  get smsSegmentParts(): string[] {
    const msg = this.textMessageData.textMessage || '';
    const link = this.textMessageData.linkText || '';
    const full = msg + (link.trim() ? '\n\n' + link : '');
    return this.buildSegmentParts(full);
  }

  get mmsCaptionSegmentParts(): string[] {
    return this.buildSegmentParts(this.textMessageData.mmsCaption || '');
  }

  get mmsBubble2SegmentParts(): string[] {
    return this.buildSegmentParts(this.textMessageData.mmsBubble2Text || '');
  }
}
